/**
 * MÓDULOS, PERMISOS Y MENÚ
 * ========================
 *
 * Dos ejes distintos filtran lo que una persona ve, y se modelan separados (política 5):
 *
 *   PLAN      ¿la entidad COMPRÓ este módulo?        → comercial  (Plan + ModuloEntidad)
 *   PERMISOS  ¿esta persona puede usarlo?            → organizacional (Rol.permisos)
 *
 * El menú efectivo es la intersección, y se CALCULA de los permisos: no hay un archivo de
 * menú aparte que se pueda desincronizar y llevar a un 403. Separados, además, permiten
 * decir "esto no está en tu plan" —que es una venta— en vez de "no tenés permiso".
 *
 * El vocabulario de permisos es código porque cada uno protege endpoints que son código.
 * Lo que es DATO es qué permisos tiene cada rol: eso lo arma cada entidad.
 */

export type ClaveModulo =
  | 'TABLERO'
  | 'CLIENTES'
  | 'INVENTARIO'
  | 'VENTAS'
  | 'COBRANZAS'
  | 'FACTURAS'
  | 'AUDITORIA'
  | 'CONFIGURACION';

export interface DefinicionModulo {
  clave: ClaveModulo;
  nombre: string;
  descripcion: string;
  ruta: string;
  icono: string;
  /** Los módulos base vienen con cualquier plan: sin configuración no se puede operar. */
  base: boolean;
}

export const MODULOS: readonly DefinicionModulo[] = [
  { clave: 'TABLERO', nombre: 'Tablero', descripcion: 'Vencimientos, cobranza y alertas de un vistazo', ruta: '/', icono: 'tablero', base: true },
  { clave: 'CLIENTES', nombre: 'Clientes', descripcion: 'Padrón y cuenta corriente de cada cliente', ruta: '/clientes', icono: 'clientes', base: false },
  { clave: 'INVENTARIO', nombre: 'Inventario', descripcion: 'Productos, existencias y movimientos', ruta: '/inventario', icono: 'inventario', base: false },
  { clave: 'VENTAS', nombre: 'Ventas', descripcion: 'Ventas en cuotas con su cuotero', ruta: '/ventas', icono: 'ventas', base: false },
  { clave: 'COBRANZAS', nombre: 'Cobranzas', descripcion: 'Cuotas a cobrar, pagos y recibos', ruta: '/cobranzas', icono: 'cobranzas', base: false },
  { clave: 'FACTURAS', nombre: 'Facturas', descripcion: 'Registro de facturas emitidas', ruta: '/facturas', icono: 'facturas', base: false },
  { clave: 'AUDITORIA', nombre: 'Auditoría', descripcion: 'Quién hizo qué y cuándo', ruta: '/auditoria', icono: 'auditoria', base: false },
  { clave: 'CONFIGURACION', nombre: 'Configuración', descripcion: 'Datos de la entidad, catálogos, usuarios y roles', ruta: '/configuracion', icono: 'configuracion', base: true },
];

/** Los módulos que se venden. Los base no figuran en un plan porque vienen siempre. */
export const MODULOS_VENDIBLES: readonly ClaveModulo[] = MODULOS.filter((m) => !m.base).map((m) => m.clave);

export interface DefinicionPermiso {
  clave: string;
  modulo: ClaveModulo;
  nombre: string;
}

export const PERMISOS = [
  { clave: 'tablero.ver', modulo: 'TABLERO', nombre: 'Ver el tablero' },

  { clave: 'clientes.ver', modulo: 'CLIENTES', nombre: 'Ver clientes y su cuenta' },
  { clave: 'clientes.crear', modulo: 'CLIENTES', nombre: 'Dar de alta clientes' },
  { clave: 'clientes.editar', modulo: 'CLIENTES', nombre: 'Editar clientes' },
  { clave: 'clientes.eliminar', modulo: 'CLIENTES', nombre: 'Dar de baja clientes' },

  { clave: 'productos.ver', modulo: 'INVENTARIO', nombre: 'Ver productos y existencias' },
  { clave: 'productos.crear', modulo: 'INVENTARIO', nombre: 'Dar de alta productos' },
  { clave: 'productos.editar', modulo: 'INVENTARIO', nombre: 'Editar productos y precios' },
  { clave: 'productos.eliminar', modulo: 'INVENTARIO', nombre: 'Dar de baja productos' },
  { clave: 'stock.ajustar', modulo: 'INVENTARIO', nombre: 'Cargar entradas y ajustes de stock' },
  { clave: 'categorias.gestionar', modulo: 'INVENTARIO', nombre: 'Gestionar categorías' },

  { clave: 'ventas.ver', modulo: 'VENTAS', nombre: 'Ver ventas y cuotas' },
  { clave: 'ventas.crear', modulo: 'VENTAS', nombre: 'Registrar ventas' },
  { clave: 'ventas.anular', modulo: 'VENTAS', nombre: 'Anular ventas' },
  { clave: 'cuotas.reprogramar', modulo: 'VENTAS', nombre: 'Reprogramar vencimientos' },

  { clave: 'pagos.ver', modulo: 'COBRANZAS', nombre: 'Ver pagos y cuotas a cobrar' },
  { clave: 'pagos.registrar', modulo: 'COBRANZAS', nombre: 'Registrar pagos' },
  { clave: 'pagos.anular', modulo: 'COBRANZAS', nombre: 'Anular pagos' },
  { clave: 'mora.condonar', modulo: 'COBRANZAS', nombre: 'Condonar mora' },
  { clave: 'recibos.ver', modulo: 'COBRANZAS', nombre: 'Ver e imprimir recibos' },

  { clave: 'facturas.ver', modulo: 'FACTURAS', nombre: 'Ver facturas' },
  { clave: 'facturas.registrar', modulo: 'FACTURAS', nombre: 'Registrar facturas' },
  { clave: 'facturas.anular', modulo: 'FACTURAS', nombre: 'Anular facturas' },

  { clave: 'auditoria.ver', modulo: 'AUDITORIA', nombre: 'Consultar la auditoría' },

  { clave: 'entidad.configurar', modulo: 'CONFIGURACION', nombre: 'Editar los datos y reglas de la entidad' },
  { clave: 'medios.gestionar', modulo: 'CONFIGURACION', nombre: 'Gestionar medios de pago' },
  { clave: 'planes.gestionar', modulo: 'CONFIGURACION', nombre: 'Gestionar planes de financiación' },
  { clave: 'usuarios.gestionar', modulo: 'CONFIGURACION', nombre: 'Gestionar usuarios' },
  { clave: 'roles.gestionar', modulo: 'CONFIGURACION', nombre: 'Gestionar roles y permisos' },
] as const satisfies readonly DefinicionPermiso[];

export type ClavePermiso = (typeof PERMISOS)[number]['clave'];

/** '*' concede todos los permisos, incluidos los que se agreguen después. */
export const TODOS_LOS_PERMISOS = '*';

export function esPermisoValido(clave: string): boolean {
  return clave === TODOS_LOS_PERMISOS || PERMISOS.some((p) => p.clave === clave);
}

export function tienePermiso(permisos: readonly string[], requerido: ClavePermiso): boolean {
  return permisos.includes(TODOS_LOS_PERMISOS) || permisos.includes(requerido);
}

export function moduloDePermiso(clave: string): ClaveModulo | null {
  return PERMISOS.find((p) => p.clave === clave)?.modulo ?? null;
}

/**
 * Módulos habilitados para una entidad: los base siempre, los vendibles según el plan, y
 * las banderas mandan sobre el plan en los dos sentidos.
 */
export function modulosHabilitados(
  modulosDelPlan: readonly string[],
  banderas: ReadonlyArray<{ clave: string; habilitado: boolean }>,
): ClaveModulo[] {
  const delPlan = new Set(modulosDelPlan);
  const porClave = new Map(banderas.map((b) => [b.clave, b.habilitado]));
  return MODULOS.filter((modulo) => {
    if (modulo.base) return true;
    return porClave.get(modulo.clave) ?? delPlan.has(modulo.clave);
  }).map((m) => m.clave);
}

export interface EntradaMenu {
  clave: ClaveModulo;
  nombre: string;
  ruta: string;
  icono: string;
  /** Visible pero no contratado: se muestra con candado y lleva a "sumalo a tu plan". */
  bloqueado: boolean;
}

/**
 * El menú es una PROYECCIÓN de permisos ∩ módulos. Un módulo aparece si la persona tiene al
 * menos un permiso de ese módulo. Si no está en el plan, solo quien administra (permiso
 * '*') lo ve con candado: a un vendedor no se le ofrecen upgrades.
 */
export function calcularMenu(
  permisos: readonly string[],
  habilitados: readonly ClaveModulo[],
): EntradaMenu[] {
  const todo = permisos.includes(TODOS_LOS_PERMISOS);
  const deModulo = new Set(permisos.map(moduloDePermiso).filter(Boolean));
  return MODULOS.filter((modulo) => todo || deModulo.has(modulo.clave))
    .map((modulo) => ({
      clave: modulo.clave,
      nombre: modulo.nombre,
      ruta: modulo.ruta,
      icono: modulo.icono,
      bloqueado: !habilitados.includes(modulo.clave),
    }))
    .filter((entrada) => !entrada.bloqueado || todo);
}

/** Permisos efectivos: los del rol, recortados a los módulos habilitados. */
export function permisosEfectivos(
  permisosDelRol: readonly string[],
  habilitados: readonly ClaveModulo[],
): string[] {
  const todos = permisosDelRol.includes(TODOS_LOS_PERMISOS)
    ? PERMISOS.map((p) => p.clave as string)
    : permisosDelRol.filter(esPermisoValido);
  return todos.filter((clave) => {
    const modulo = moduloDePermiso(clave);
    return modulo !== null && habilitados.includes(modulo);
  });
}

/**
 * Roles con los que nace una entidad. Son un punto de partida: la entidad los edita, los
 * clona o crea los suyos. No son una jaula.
 */
export const ROLES_PLANTILLA: ReadonlyArray<{ nombre: string; descripcion: string; permisos: string[] }> = [
  {
    nombre: 'Administración',
    descripcion: 'Acceso total a la entidad, incluidos usuarios, roles y auditoría.',
    permisos: [TODOS_LOS_PERMISOS],
  },
  {
    nombre: 'Ventas',
    descripcion: 'Carga ventas y clientes, consulta el inventario y cobra.',
    permisos: [
      'tablero.ver', 'clientes.ver', 'clientes.crear', 'clientes.editar', 'productos.ver',
      'ventas.ver', 'ventas.crear', 'pagos.ver', 'pagos.registrar', 'recibos.ver',
    ],
  },
  {
    nombre: 'Cobranza',
    descripcion: 'Registra pagos y emite recibos. No carga ventas.',
    permisos: ['tablero.ver', 'clientes.ver', 'ventas.ver', 'pagos.ver', 'pagos.registrar', 'recibos.ver'],
  },
  {
    nombre: 'Depósito',
    descripcion: 'Gestiona productos y existencias.',
    permisos: [
      'tablero.ver', 'productos.ver', 'productos.crear', 'productos.editar', 'stock.ajustar',
      'categorias.gestionar',
    ],
  },
  {
    nombre: 'Consulta',
    descripcion: 'Solo lectura de todo lo operativo.',
    permisos: ['tablero.ver', 'clientes.ver', 'productos.ver', 'ventas.ver', 'pagos.ver', 'recibos.ver', 'facturas.ver'],
  },
];

/** Medios de pago con los que nace una entidad. La entidad los edita desde su configuración. */
export const MEDIOS_PAGO_INICIALES: ReadonlyArray<{ nombre: string; requiereComprobante: boolean; requiereReferencia: boolean; orden: number }> = [
  { nombre: 'Efectivo', requiereComprobante: false, requiereReferencia: false, orden: 1 },
  { nombre: 'Transferencia bancaria', requiereComprobante: true, requiereReferencia: true, orden: 2 },
  { nombre: 'Tarjeta de débito', requiereComprobante: false, requiereReferencia: true, orden: 3 },
  { nombre: 'Billetera electrónica', requiereComprobante: true, requiereReferencia: true, orden: 4 },
];
